import assign from 'lodash/assign'
import merge from 'lodash/merge'

import Bugsnag from '@bugsnag/js'
import { ICreditCard } from 'checkout/hooks/usePayments'
import { IOrder, PartialOrder } from 'checkout/types/IOrder'
import { UserType } from 'shared/types/User'
import { Order } from 'shared/types/Order'

export type PanelNames =
  | 'auth'
  | 'guestCheckoutAuth'
  | 'expressCheckout'
  | 'shippingAddress'
  | 'shippingMethod'
  | 'paymentMethod'
  | 'promotion'

export interface IPanelState {
  complete: boolean
  disabled?: boolean
  error?: string
  isEditing: boolean
  loading: boolean
  view?: string
}

export interface ICheckoutState {
  applePayAvailable?: boolean
  creditCards?: ICreditCard[]
  creditCardVerificationEnabled: boolean
  errorModalMessage?: string
  isGuestCheckout: boolean
  order?: IOrder | Order
  orderLoading: boolean
  orderReadyForPurchase: boolean
  orderSubmitting: boolean
  panels: { [panelName in PanelNames]: IPanelState }
  userType: UserType
}

export const INITIAL_CHECKOUT_STATE: ICheckoutState = {
  panels: {
    auth: {
      complete: false,
      isEditing: true,
      loading: false,
      view: 'login',
    },
    guestCheckoutAuth: {
      complete: false,
      isEditing: true,
      loading: false,
    },
    expressCheckout: {
      isEditing: false,
      disabled: true,
      complete: false,
      loading: false,
    },
    shippingAddress: {
      isEditing: false,
      disabled: true,
      complete: false,
      loading: false,
      view: 'new',
    },
    shippingMethod: {
      isEditing: false,
      disabled: true,
      complete: false,
      loading: false,
    },
    paymentMethod: {
      isEditing: false,
      disabled: true,
      complete: false,
      loading: false,
      view: 'new',
    },
    promotion: {
      isEditing: false,
      disabled: true,
      complete: false,
      loading: false,
    },
  },
  creditCardVerificationEnabled: false,
  isGuestCheckout: false,
  orderLoading: true,
  orderReadyForPurchase: false,
  orderSubmitting: false,
  userType: 'guest',
}

/*****************************************************************/
// Action Payload Interfaces
// Reducer actions have the shape { type: string, payload?: any }
// These interfaces describe the shape of the payload
// for each action and help downstream components
// enforce correct payload shapes
/*****************************************************************/
export interface IAction {
  type: string
}
interface IPanelUpdatePayload extends Partial<IPanelState> {
  panelName: PanelNames
}
export interface IPanelUpdateAction extends IAction {
  payload: IPanelUpdatePayload
}
export interface IPanelEditAction extends IAction {
  payload: PanelNames
}
export interface IPanelLoadingAction extends IAction {
  payload?: boolean
}
export interface IOrderSuccessAction extends IAction {
  payload: IOrder | Order
}
export interface IPartialOrderSuccessAction extends IAction {
  payload: PartialOrder
}
export interface ICreditCardSuccessAction extends IAction {
  payload: ICreditCard[]
}
export interface IErrorAction extends IAction {
  payload: string
}
export interface IVerifyCreditCardSuccess extends IAction {
  payload: boolean
}
export interface IApplePayEnabled extends IAction {
  payload: boolean
}
export interface IUserTypeAction extends IAction {
  payload: UserType
}

/*****************************************************************/
// Action Type Guards
// Type guards allow us to do two things:
// 1) Sort actions into useful groups that share state mutation
// 2) Associate interfaces to actions
//
// You can read more about type guards in the typescript docs:
// https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
/*****************************************************************/
const isPanelLoading = (action: IAction): action is IPanelLoadingAction =>
  action.type === 'AddressRequest' ||
  action.type === 'ShippingServiceLevelRequest' ||
  action.type === 'paymentMethodRequest' ||
  action.type === 'promotionLoading'
const isPanelUpdate = (action: IAction): action is IPanelUpdateAction =>
  action.type === 'panelUpdate'
const isPanelEdit = (action: IAction): action is IPanelEditAction => action.type === 'panelEdit'
const isPanelError = (action: IAction): action is IErrorAction =>
  action.type === 'AddressRequestFailure' ||
  action.type === 'ShippingServiceLevelRequestFailure' ||
  action.type === 'paymentMethodRequestFailure' ||
  action.type === 'promotionRequestFailure'
const isOrderLoading = (action: IAction) =>
  action.type === 'orderRequest' || action.type === 'expressCheckoutRequest'
const isPartialOrderSuccess = (action: IAction): action is IPartialOrderSuccessAction =>
  action.type === 'priceEstimateRequestSuccess' ||
  action.type === 'orderFromQueryParamsSuccess' ||
  action.type === 'updateOrderCurrencySuccess'
const isOrderRequestSuccess = (action: IAction): action is IOrderSuccessAction =>
  action.type === 'AddressRequestSuccess' ||
  action.type === 'ShippingServiceLevelRequestSuccess' ||
  action.type === 'orderRequestSuccess' ||
  action.type === 'paymentMethodRequestSuccess' ||
  action.type === 'promotionRequestSuccess' ||
  action.type === 'removePromotionRequestSuccess'
const isOrderRequestFailure = (action: IAction): action is IErrorAction =>
  action.type === 'orderRequestFailure'
const isPurchaseOrderFailure = (action: IAction): action is IErrorAction =>
  action.type === 'expressCheckoutFailure' || action.type === 'purchaseOrderRequestFailure'
const isApplePayAvailable = (action: IAction): action is IApplePayEnabled =>
  action.type === 'applePayAvailable'
const isCreditCardRequestSuccess = (action: IAction): action is ICreditCardSuccessAction =>
  action.type === 'creditCardRequestSuccess'
const isVerifyCreditCardSuccess = (action: IAction): action is IVerifyCreditCardSuccess =>
  action.type === 'verifyCreditCardSuccess'
const isUserTypeUpdate = (action: IAction): action is IUserTypeAction =>
  action.type === 'UserTypeUpdate'

export const checkoutReducer = (state: ICheckoutState, action: IAction) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.info(`CheckoutStateAction: ${action.type}`, (action as IErrorAction)?.payload)
  }

  /*****************************************************************/
  //  _____                 _                _   _
  // |  __ \               | |     /\       | | (_)
  // | |__) |_ _ _ __   ___| |    /  \   ___| |_ _  ___  _ __  ___
  // |  ___/ _` | '_ \ / _ \ |   / /\ \ / __| __| |/ _ \| '_ \/ __|
  // | |  | (_| | | | |  __/ |  / ____ \ (__| |_| | (_) | | | \__ \
  // |_|   \__,_|_| |_|\___|_| /_/    \_\___|\__|_|\___/|_| |_|___/
  //
  /*****************************************************************/

  /****************************/
  // Panel Loading Actions
  // These actions target a CheckoutPanel using panelNamesByActionType
  // and set that panel to a loading state. The actions are typically fired
  // before async events like an API call then views in CheckoutPanel
  // use the <Spinner /> loading component and display specialized text.
  /****************************/
  if (isPanelLoading(action)) {
    const panelNamesByActionType = new Map<string, PanelNames>([
      ['AddressRequest', 'shippingAddress'],
      ['ShippingServiceLevelRequest', 'shippingMethod'],
      ['paymentMethodRequest', 'paymentMethod'],
      ['promotionLoading', 'promotion'],
    ])
    const panelName = panelNamesByActionType.get(action.type)
    if (panelName) {
      state.panels[panelName].complete = false
      state.panels[panelName].error = undefined
      state.panels[panelName].loading = typeof action.payload === 'boolean' ? action.payload : true
    }
  }

  /****************************/
  // Update Panel Action
  // This action is available to downstream components via the updatePanel
  // function created/exported in CheckoutContextProvider. It allows
  // components to modify any CheckoutPanel state. While a bit generalized
  // it's a useful tool with an easy to understand API
  /****************************/
  if (isPanelUpdate(action)) {
    const { panelName, ...panelUpdates } = action.payload
    const panelCompletedUpdates = panelUpdates.complete && {
      disabled: false,
      error: undefined,
      isEditing: false,
      loading: false,
    }
    state.panels[panelName] = merge(state.panels[panelName], panelUpdates, panelCompletedUpdates)
  }

  /****************************/
  // Panel Edit Action
  // This action is triggered by CheckoutPanel when the panel edit
  // button is clicked. We trigger an action to allow different
  // behavior in certain situations
  /****************************/
  if (isPanelEdit(action)) {
    const panel = state.panels[action.payload]
    panel.isEditing = true
    if (panel.view === 'verification') {
      panel.view = 'saved'
    }
  }

  /****************************/
  // Panel Error Actions
  // These actions target a CheckoutPanel using panelNamesByActionType
  // and set that panel to an error state. When in an error state
  // CheckoutPanel displays an <ErrorMessage /> above the main panel.
  // Often error actions are fired when API calls fail and need to
  // display feedback to the user.
  /****************************/
  if (isPanelError(action)) {
    const panelNamesByActionType = new Map<string, PanelNames>([
      ['AddressRequestFailure', 'shippingAddress'],
      ['ShippingServiceLevelRequestFailure', 'shippingMethod'],
      ['paymentMethodRequestFailure', 'paymentMethod'],
      ['promotionRequestFailure', 'promotion'],
    ])
    const panelName = panelNamesByActionType.get(action.type)
    if (panelName) {
      state.orderLoading = false
      state.panels[panelName].complete = false
      state.panels[panelName].disabled = false
      state.panels[panelName].error = action.payload
      state.panels[panelName].isEditing = true
      state.panels[panelName].loading = false
    }
  }

  /*********************************************************************/
  //   ____          _                          _   _
  //  / __ \        | |               /\       | | (_)
  // | |  | |_ __ __| | ___ _ __     /  \   ___| |_ _  ___  _ __  ___
  // | |  | | '__/ _` |/ _ \ '__|   / /\ \ / __| __| |/ _ \| '_ \/ __|
  // | |__| | | | (_| |  __/ |     / ____ \ (__| |_| | (_) | | | \__ \
  //  \____/|_|  \__,_|\___|_|    /_/    \_\___|\__|_|\___/|_| |_|___/
  //
  /*********************************************************************/

  /****************************/
  // Order Loading Actions
  // These actions trigger a global loading indicator
  // on the checkout page
  /****************************/
  if (isOrderLoading(action)) {
    state.orderLoading = true
  }

  /****************************/
  // Order Success Actions
  // All actions that return full order objects are routed to this block.
  // The reducer can determine correct page state for all panels from
  // that order object
  /****************************/
  if (isOrderRequestSuccess(action)) {
    // begin by removing the global loading indicator
    // and providing the order object downstream
    state.orderLoading = false
    // all payloads in this block should be a FULL order object
    if (action.type === 'removePromotionRequestSuccess') {
      // When removing a promo code, update promoCodeLedgerId with a null value
      // must use assign to allow null values to overwrite any existing truthy values
      state.order = assign(state.order, action.payload)
    } else {
      // Standard updates to state require merge to update existing values
      state.order = merge(state.order, action.payload)
    }
    // assure pricingBreakdown are always latest from sneakers
    state.order.pricingBreakdown = action.payload.pricingBreakdown
    // assure shippingServiceLevelOptions are always latest from sneakers
    state.order.shippingServiceLevelOptions = action.payload.shippingServiceLevelOptions

    // auth + guestCheckoutAuth panels
    // a user always exists if an order
    // has been created / returned
    if (state.isGuestCheckout) {
      state.panels.guestCheckoutAuth = {
        complete: true,
        disabled: false,
        isEditing: false,
        loading: false,
      }
      state.panels.auth = {
        complete: false,
        disabled: true,
        isEditing: false,
        loading: false,
      }
    } else {
      state.panels.auth = {
        complete: true,
        disabled: false,
        isEditing: false,
        loading: false,
        view: 'loggedIn',
      }
    }

    // shippingAddress panel
    const hasShippingAddress = !!state.order.shippingAddressId || !!state.order?.addressId
    state.panels.shippingAddress = {
      complete: hasShippingAddress,
      isEditing: !hasShippingAddress,
      loading: false,
      view: hasShippingAddress ? 'saved' : 'new',
    }

    // shippingMethod panel
    // forces the user to pick a shippingMethod only when necessary via shouldConfirmShippingMethod
    const hasShippingServiceLevel = !!state.order.shippingServiceLevel
    // prettier-ignore
    const hasMultipleShippingMethods =
      (state.order?.shippingServiceLevelOptions || []).filter(method => method.available).length > 1
    const shouldConfirmShippingMethod =
      /orderRequestSuccess|AddressRequestSuccess/.test(action.type) &&
      hasMultipleShippingMethods &&
      action.payload.klarnaReason !== 'success'
    const shippingServiceLevelComplete =
      hasShippingAddress && hasShippingServiceLevel && !shouldConfirmShippingMethod
    state.panels.shippingMethod = {
      complete: shippingServiceLevelComplete,
      disabled: !hasShippingAddress,
      isEditing: !shippingServiceLevelComplete,
      loading: false,
    }

    // paymentMethod panel
    // forces user to pick a paymentMethod.
    // Klarna and PayPal users complete purchase on their sites
    const hasPaymentMethod = !!state.order.billingInfoId
    const isValidPaymentType =
      state.order.billingInfo?.paymentType !== 'klarna' &&
      state.order.billingInfo?.paymentType !== 'paypal_v2'
    const verificationNeeded = state.creditCardVerificationEnabled && state.order.verificationNeeded
    const paymentMethodPanelComplete =
      shippingServiceLevelComplete && hasPaymentMethod && isValidPaymentType && !verificationNeeded
    state.panels.paymentMethod = {
      complete: paymentMethodPanelComplete,
      disabled: !shippingServiceLevelComplete,
      isEditing: !paymentMethodPanelComplete,
      loading: false,
      view: verificationNeeded ? 'verification' : hasPaymentMethod ? 'saved' : 'new',
    }

    // promotion panel
    const hasPromotion = !!state.order.promotion || !!state.order.promoCodeLedgerId
    state.panels.promotion = {
      isEditing: !hasPromotion,
      disabled: false,
      complete: hasPromotion,
      loading: false,
    }
  }

  /****************************/
  // Partial Order Actions
  // These actions provide order information to downstream components
  // but do not attempt to update panel state
  /****************************/
  if (isPartialOrderSuccess(action)) {
    state.orderLoading = false
    state.order = merge(state.order, action.payload)
    if (action?.payload?.pricingBreakdown) {
      // assure pricingBreakdown are always latest from sneakers
      state.order.pricingBreakdown = action.payload.pricingBreakdown
    }
  }

  /****************************/
  // Order Error Actions
  // state.errorModalMessage displays a page-wide error modal
  // that forces the user to exit checkout and try again.
  /****************************/
  if (isOrderRequestFailure(action)) {
    state.orderLoading = false
    state.errorModalMessage = action.payload
    // Since this is a major error we send it to bugsnag to
    // better understand why checkout might have errors
    Bugsnag.notify(new Error(action.payload))
  }

  /****************************/
  // Purchase Order Request Action
  // This single action is fired right after the user attempts to
  // purchase the products in checkout. The purchase order button that
  // fires this will only be enabled if state.orderReadyForPurchase === true
  /****************************/
  if (action.type === 'purchaseOrderRequest') {
    state.orderSubmitting = true
    state.panels.auth.error = undefined
    state.panels.guestCheckoutAuth.error = undefined
  }

  /****************************/
  // Purchase Order Failure Action
  // Fired when purchasing an order fails.
  // The payload contains the error message from sneakers
  /****************************/
  if (isPurchaseOrderFailure(action)) {
    state.orderLoading = false
    state.orderSubmitting = false
    state.panels.auth.error = action.payload
    state.panels.guestCheckoutAuth.error = action.payload
  }

  /****************************/
  // Express Pay Available Action
  // useExpressPayRTK/useApplePay fires this event when users can express pay
  // the action shows/hides the relevant UI
  /****************************/
  if (isApplePayAvailable(action)) {
    const applePayAvailable = action.payload
    state.applePayAvailable = applePayAvailable
    state.panels.expressCheckout = {
      complete: false,
      isEditing: false,
      disabled: !applePayAvailable,
      loading: false,
    }
  }

  /****************************/
  // Credit Card Success Action
  // The paymentMethod panel requests the user's saved credit cards
  // only when in the { view: 'saved' } state. Loading and error
  // states are handled by IPanelLoadingAction / IErrorAction.
  /****************************/
  if (isCreditCardRequestSuccess(action)) {
    state.panels.paymentMethod.loading = false
    state.creditCards = action.payload
  }

  /****************************/
  // Continue As Guest Action
  // This guest checkout specific action closes the auth panel
  // and allows guest users to begin entering their shippingAddress.
  // In the guest checkout flow a full order is created only after
  // the guest user successfully enters their shippingAddress.
  /****************************/
  if (action.type === 'continueAsGuest') {
    state.panels.guestCheckoutAuth = {
      complete: true,
      isEditing: false,
      loading: false,
    }
    state.panels.shippingAddress = {
      complete: false,
      isEditing: true,
      loading: false,
      view: state.userType === 'guest' ? 'guest' : 'new',
    }
  }

  /****************************/
  // Verify Credit Card Action
  // When the order object has verificationNeeded the user
  // will need to enter their CVV to use that paymentMethod.
  // onSuccess this event updates the panel and order object
  /****************************/
  if (isVerifyCreditCardSuccess(action)) {
    state.panels.paymentMethod = {
      complete: true,
      disabled: false,
      error: undefined,
      isEditing: false,
      loading: false,
      view: 'saved',
    }
    if (state.order) {
      state.order.verificationNeeded = false
    }
  }

  /****************************/
  // User Type Update action
  // Some actions depend on knowing the current userType.
  // This action is fired when a user logs in / out and
  // stores the userType in state.
  /****************************/
  if (isUserTypeUpdate(action)) {
    state.userType = action.payload
  }

  /****************************************************************************************/
  //                     _       ______         _____                _
  //                    | |     |  ____|       |  __ \              | |
  //  _ __ ___  __ _  __| |_   _| |__ ___  _ __| |__) |   _ _ __ ___| |__   __ _ ___  ___
  // | '__/ _ \/ _` |/ _` | | | |  __/ _ \| '__|  ___/ | | | '__/ __| '_ \ / _` / __|/ _ \
  // | | |  __/ (_| | (_| | |_| | | | (_) | |  | |   | |_| | | | (__| | | | (_| \__ \  __/
  // |_|  \___|\__,_|\__,_|\__, |_|  \___/|_|  |_|    \__,_|_|  \___|_| |_|\__,_|___/\___|
  //                        __/ |
  //                       |___/
  /****************************************************************************************/

  /*******************************/
  // state.orderReadyForPurchase
  // determines if the user can place an order.
  // The immutable logic for it runs on EVERY action.
  // This should always be the last item in the reducer
  // so the state mutations above are reflected
  /*******************************/
  const exludedAuthPanel: PanelNames = state.isGuestCheckout ? 'auth' : 'guestCheckoutAuth'
  const excludedPanels = new Set<PanelNames>(['promotion', 'expressCheckout', exludedAuthPanel])
  // prettier-ignore
  const activePanelsNotEditing = Object.keys(state.panels)
    .filter(key => excludedPanels.has(key as PanelNames) === false)
    .every(key => state.panels[key as PanelNames].isEditing === false)
  const verificationNotNeeded =
    (state.creditCardVerificationEnabled && state.order?.verificationNeeded) === false
  state.orderReadyForPurchase =
    activePanelsNotEditing &&
    verificationNotNeeded &&
    (!!state.order?.shippingAddressId || !!state.order?.addressId) &&
    !!state.order?.shippingServiceLevel &&
    !!state.order?.billingInfoId
}
